import { FC, ReactNode, useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import { trackPromoImpressions, trackPromoClick } from '../helpers/sbSharedFeaturedTracking';

// components
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import { defineMessages, FormattedMessage, FormattedNumber, useIntl } from 'dibs-react-intl';
import { SbSharedL1FeaturedTile } from '../SbSharedL1FeaturedTile/SbSharedL1FeaturedTile';

// types
import { SbSharedFeaturedDeal_itemSearch$key as ItemSearch } from './__generated__/SbSharedFeaturedDeal_itemSearch.graphql';

// styles
import styles from './SbSharedFeaturedDeal.scss';

type Vertical = 'furniture' | 'art' | 'jewelry' | 'fashion';

type Props = {
    currency: string;
    itemSearch: ItemSearch;
    position?: number;
};

const verticalMessages = defineMessages<Vertical>({
    furniture: {
        id: 'SbSharedFeaturedDeal.price.furniture',
        defaultMessage: 'Furniture under {price}',
    },
    art: {
        id: 'SbSharedFeaturedDeal.price.art',
        defaultMessage: 'Art under {price}',
    },
    jewelry: {
        id: 'SbSharedFeaturedDeal.price.jewelry',
        defaultMessage: 'Jewelry under {price}',
    },
    fashion: {
        id: 'SbSharedFeaturedDeal.price.fashion',
        defaultMessage: 'Fashion under {price}',
    },
});

const itemSearchFragment = graphql`
    fragment SbSharedFeaturedDeal_itemSearch on ItemSearchQueryConnection {
        featuredDeal {
            imageUrl
            vertical
            convertedAmountList {
                amount
                currency
                linkData {
                    ...SeoLink_linkData
                }
            }
        }
    }
`;

const CREATIVE_LABEL = 'price bucket';

export const SbSharedFeaturedDeal: FC<Props> = ({
    itemSearch: itemSearchRefRef,
    currency,
    position,
}) => {
    const intl = useIntl();
    const itemSearch = useFragment(itemSearchFragment, itemSearchRefRef);
    const { featuredDeal } = itemSearch || {};
    const { imageUrl, convertedAmountList = null, vertical } = featuredDeal || {};
    const { linkData, amount } =
        convertedAmountList?.find(convertedAmount => convertedAmount?.currency === currency) || {};

    useEffect(() => {
        if (amount && vertical) {
            trackPromoImpressions([CREATIVE_LABEL], position);
        }
    }, [amount, vertical, position]);

    if (!amount || !vertical || !linkData) {
        return null;
    }

    return (
        <SbSharedL1FeaturedTile
            mainImage={imageUrl}
            heading={
                <FormattedMessage
                    id="SbSharedFeaturedDeal.heading"
                    defaultMessage="<span>Find </span>Extraordinary Values"
                    values={{
                        span: (content: ReactNode[]) => (
                            <span className={styles.hideOnMobile}>{content}</span>
                        ),
                    }}
                />
            }
            subHeading={intl.formatMessage(verticalMessages[vertical as Vertical], {
                price: (
                    <FormattedNumber
                        value={amount}
                        style="currency"
                        currency={currency}
                        minimumFractionDigits={0}
                    />
                ),
            })}
        >
            {({ className, children }) => (
                <SeoLink
                    className={className}
                    linkData={linkData}
                    onClick={() => trackPromoClick(CREATIVE_LABEL, position)}
                >
                    {children}
                </SeoLink>
            )}
        </SbSharedL1FeaturedTile>
    );
};
